import type { ModuleQuestions } from '@ifs-gcp/core/models';

import { DEFAULT_LOCATION } from '@/hooks/useGoogleMaps';

export const getAnswerString = (questions: ModuleQuestions, key: string) =>
  typeof questions[key]?.answer === 'string' ? (questions[key].answer as string) : '';

type GetInitialLocationProps = {
  companyGpsLatitude?: string;
  companyGpsLongitude?: string;
  companyAddress?: string;
};

/*
 * Initial location is prioritized in the following order:
 * 1. Already entered GPS data
 * 2. Company address
 * 3. Default location
 * */
export const getInitialLocation = async ({
  companyGpsLatitude,
  companyGpsLongitude,
  companyAddress,
}: GetInitialLocationProps): Promise<GeoLocation & { address: string }> => {
  let initialLocationCoords = DEFAULT_LOCATION;
  let initialLocationAddress = '';

  if (
    companyGpsLatitude &&
    companyGpsLongitude &&
    !Number.isNaN(Number(companyGpsLatitude)) &&
    !Number.isNaN(Number(companyGpsLongitude))
  ) {
    initialLocationCoords = {
      latitude: Number(companyGpsLatitude),
      longitude: Number(companyGpsLongitude),
    };
  } else if (companyAddress) {
    initialLocationAddress = companyAddress;

    const geocoderResults = await geocodeLocation({
      address: companyAddress,
    }).catch(() => {});

    const companyAddressLocation = geocoderResults?.[0]?.geometry?.location;

    initialLocationCoords = companyAddressLocation
      ? {
          latitude: companyAddressLocation.lat(),
          longitude: companyAddressLocation.lng(),
        }
      : DEFAULT_LOCATION;
  }

  if (!initialLocationAddress) {
    const geocoderResults = await geocodeLocation({
      location: {
        lat: initialLocationCoords.latitude,
        lng: initialLocationCoords.longitude,
      },
    }).catch(() => {});

    initialLocationAddress = geocoderResults?.[0]?.formatted_address || '';
  }

  return {
    ...initialLocationCoords,
    address: initialLocationAddress,
  };
};

export const formatLocationParam = (param: number) => String(param.toFixed(6));

// GOOGLE MAPS UTILS

export interface GeoLocation {
  latitude: number;
  longitude: number;
  address?: string;
}

let _geocoder: google.maps.Geocoder | null = null;

export const initializeGeocoder = () => {
  if (_geocoder) {
    return _geocoder;
  }

  _geocoder = new window.google.maps.Geocoder();
  return _geocoder;
};

export const cleanupMapResource = (resource: google.maps.MVCObject | null) => {
  if (resource) {
    resource.unbindAll(); // Remove all event listeners
  }
};

export const cleanupMapMarker = (m: google.maps.Marker | null) => {
  if (m) {
    m.setMap(null); // Remove the old marker
    cleanupMapResource(m);
  }
};

export const geocodeLocation = async (request: google.maps.GeocoderRequest) => {
  const geocoder = initializeGeocoder();

  return new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
    geocoder.geocode(request, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        resolve(results);
      } else {
        reject(new Error(status));
      }
    });
  });
};

export const createMapMarker = (
  map: google.maps.Map | null,
  location: GeoLocation,
  options: google.maps.MarkerOptions = {},
) => {
  if (!map) {
    throw new Error('A valid Map instance is required to create a marker');
  }

  return new window.google.maps.Marker({
    position: {
      lat: location.latitude,
      lng: location.longitude,
    },
    map,
    draggable: true,
    ...options,
  });
};
